import type {
	Cluster,
	ClustersRequestFilters
} from '~/core/entities/api/ApiClusters';

import { useEventBus } from '@vueuse/core';
import { extractErrorLogContext, useLogger } from '~/composables/useLogger';
import { EventBusKeys } from '~/core/helpers/symbols';
import { createSafeDebouncedFunction } from '~/utils';
import { nuxtClustersRepository } from '~~/adapters/repositories/nuxt/nuxtClustersRepository';

interface UseMapClustersOptions {
	gmapRef: Ref<{ mapInstance?: google.maps.Map } | null>;
	clustersFilters?: {
		[key: string]: string | boolean | number;
	} & Partial<ClustersRequestFilters>;
}

export function useMapClusters({
	gmapRef,
	clustersFilters
}: UseMapClustersOptions) {
	const logger = useLogger('MapClusters');
	const clusters = ref<Cluster[]>([]);
	const isUpdatingClusters = ref(false);
	const selectedClustersData = ref<Set<string>>(new Set());
	const selectedClustersCache = ref<Map<string, Cluster>>(new Map());
	const markersKey = ref(0);

	let abortController: AbortController | null = null;

	const autoZoomBus = useEventBus(EventBusKeys.autoZoom);

	function normalizeClusterCoordinates(coordinates: [number, number]): {
		lat: number;
		lng: number;
	} {
		const [first, second] = coordinates;
		return { lat: first, lng: second };
	}

	function handleClusterSelectionChanged(data: {
		clusterId: string;
		isSelected: boolean;
	}) {
		if (data.isSelected) {
			selectedClustersData.value.add(data.clusterId);
			const cluster = clusters.value.find(
				c => c.geometry.id === data.clusterId
			);
			if (cluster) {
				selectedClustersCache.value.set(data.clusterId, cluster);
			}
		} else {
			selectedClustersData.value.delete(data.clusterId);
			selectedClustersCache.value.delete(data.clusterId);
		}
	}

	function handleClusterDeselectedByResolution(data: { clusterId: string }) {
		selectedClustersData.value.delete(data.clusterId);
		selectedClustersCache.value.delete(data.clusterId);
	}

	function clearSelectedClusters() {
		selectedClustersData.value.clear();
		selectedClustersCache.value.clear();
	}

	const selectedH3Ids = computed(() => {
		return Array.from(selectedClustersData.value);
	});

	function incrementMarkersKey() {
		markersKey.value += 1;
	}

	function getMapDimensions(): { width?: number; height?: number } {
		if (!gmapRef.value?.mapInstance) {
			return {};
		}

		try {
			const mapDiv = gmapRef.value.mapInstance.getDiv();
			if (mapDiv) {
				return {
					width: mapDiv.offsetWidth || undefined,
					height: mapDiv.offsetHeight || undefined
				};
			}
		} catch (error) {
			logger.warn(
				'Error getting map dimensions',
				extractErrorLogContext(error)
			);
		}

		return {};
	}

	const updateClusters = async (filters: ClustersRequestFilters) => {
		let { bounds, zoom } = filters;

		const includePropertyIds = clustersFilters?.includePropertyIds;

		if (zoom && zoom < 7) {
			zoom = 7;
		}

		if (abortController) {
			abortController.abort('Request superseded by newer request');
		}

		abortController = new AbortController();
		const currentController = abortController;

		try {
			isUpdatingClusters.value = true;

			const mapDimensions = getMapDimensions();

			const selectedFilters = {
				...filters,
				bounds: bounds || undefined,
				zoom: zoom || undefined,
				mapWidth: mapDimensions.width,
				mapHeight: mapDimensions.height,
				transactionType: filters.transactionType,
				includePropertyIds: includePropertyIds,
				includeH3Clusters: true,
				showHideProperties: clustersFilters?.showHideProperties,
				showUnpublishedProperties: clustersFilters?.showUnpublishedProperties,
				wishlistId: clustersFilters?.wishlistId
			};

			selectedFilters.h3Ids = [];
			selectedFilters.commercialIds = [];

			const [result, error] = await nuxtClustersRepository.list(
				{
					...selectedFilters
				},
				currentController.signal
			);

			if (currentController.signal.aborted) {
				return;
			}

			if (error) {
				logger.error('Error while fetching clusters from repository', {
					source: 'useMapClusters',
					...extractErrorLogContext(error)
				});
				return;
			}

			if (currentController === abortController) {
				const newClustersMap = new Map<string, Cluster>();
				result.data.forEach(cluster => {
					newClustersMap.set(cluster.geometry.id, cluster);
				});

				const selectedClusterIds = Array.from(selectedClustersData.value);
				selectedClusterIds.forEach(clusterId => {
					const updatedCluster = newClustersMap.get(clusterId);
					if (updatedCluster) {
						selectedClustersCache.value.set(clusterId, updatedCluster);
					}
				});

				clusters.value = result.data;
				incrementMarkersKey();

				if (result.isAutoZoom && result.calculatedZoom) {
					autoZoomBus.emit(result.calculatedZoom);
				}
			}
		} catch (err) {
			if (!currentController.signal.aborted) {
				logger.error('Unexpected error in updateClusters', {
					source: 'useMapClusters',
					...extractErrorLogContext(err)
				});
			}
		} finally {
			if (
				currentController === abortController &&
				!currentController.signal.aborted
			) {
				isUpdatingClusters.value = false;
			}

			// Clean up the abort controller if this was the current one
			if (currentController === abortController) {
				abortController = null;
			}
		}
	};

	const debouncedUpdateClusters = createSafeDebouncedFunction(
		updateClusters,
		300,
		{
			maxWait: 1000
		}
	);

	onUnmounted(() => {
		if (abortController) {
			abortController.abort('Component unmounted');
		}
	});

	return {
		clusters,
		selectedClustersData: readonly(selectedClustersData),
		selectedH3Ids,
		markersKey,
		updateClusters,
		handleClusterSelectionChanged,
		handleClusterDeselectedByResolution,
		normalizeClusterCoordinates,
		clearSelectedClusters
	};
}
